import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CheckCircle, Clock, MapPin, Users, Shield, Phone, ArrowRight, Star, Home } from 'lucide-react';

const VaaSLandlordPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900/50 opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              Premium Service
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Viewings as a Service
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Let our professional team conduct property viewings on your behalf. 
              No more scheduling conflicts, no more wasted time. We handle everything.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/landlords/dashboard"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-all"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-semibold transition-all"
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Our streamlined process makes property viewings effortless for landlords
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {[
            {
              step: "1",
              icon: Home,
              title: "List Your Property",
              description: "Add your property with photos and 360° tour. Enable VaaS as an add-on."
            },
            {
              step: "2",
              icon: Calendar,
              title: "We Schedule",
              description: "Our system manages availability by county. Tenants book directly."
            },
            {
              step: "3",
              icon: Users,
              title: "We Conduct",
              description: "Professional staff conduct viewings, answer questions, highlight features."
            },
            {
              step: "4",
              icon: CheckCircle,
              title: "You Decide",
              description: "Receive feedback and applications. Choose your ideal tenant."
            }
          ].map((item, index) => (
            <div key={index} className="relative">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-slate-800/30 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Why Choose VaaS?</h2>
              <div className="space-y-6">
                {[
                  {
                    icon: Clock,
                    title: "Save Time",
                    description: "No more coordinating schedules or travelling to properties. We handle all viewings."
                  },
                  {
                    icon: Shield,
                    title: "Professional Presentation",
                    description: "Trained staff present your property in the best light, answering all tenant questions."
                  },
                  {
                    icon: Users,
                    title: "Verified Tenants Only",
                    description: "Only pre-verified tenants can book viewings, reducing time-wasters."
                  },
                  {
                    icon: MapPin,
                    title: "County Coverage",
                    description: "We cover properties across Scotland with local teams in each area."
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                      <p className="text-slate-400">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-white mb-6">Portfolio Landlords</h3>
              <p className="text-slate-400 mb-6">
                Managing multiple properties? VaaS becomes even more valuable:
              </p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  15% discount for 5+ properties
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Dedicated account manager
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Priority scheduling
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Monthly viewing reports
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
          <p className="text-slate-400">Pay only for viewings conducted. No hidden fees.</p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Standard Pricing</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700/50">
                      <span className="text-slate-400">Admin Fee (one-time)</span>
                      <span className="text-white font-semibold">£50</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700/50">
                      <span className="text-slate-400">Hourly Rate</span>
                      <span className="text-white font-semibold">£35/hour</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700/50">
                      <span className="text-slate-400">Minimum Charge</span>
                      <span className="text-white font-semibold">30 mins</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700/50">
                      <span className="text-slate-400">Travel</span>
                      <span className="text-white font-semibold">45p/mile</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Weekend Premium</span>
                      <span className="text-white font-semibold">+25%</span>
                    </div>
                  </div>
                </div>
                <div className="bg-emerald-500/10 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-emerald-400 mb-4">Included in Fully Managed?</h3>
                  <p className="text-slate-300 mb-4">
                    If you&apos;re on our Fully Managed tier (12% commission), you receive:
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      2 free VaaS viewings per property
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      50% off additional viewings
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-slate-700/30 px-8 py-6">
              <p className="text-slate-400 text-sm text-center">
                * Pricing subject to admin matrix configuration. Contact us for custom enterprise rates.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Save Time?</h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Add Viewings as a Service to any property listing. Start focusing on what matters - finding the right tenant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/landlords/dashboard"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 rounded-xl font-semibold hover:bg-emerald-50 transition-all"
            >
              Enable VaaS Now
            </Link>
            <a
              href="tel:+441onal"
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-700/50 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all"
            >
              <Phone className="w-5 h-5" />
              Speak to Us
            </a>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-center text-slate-500 text-sm">
          Viewings as a Service is available in supported counties. 
          <span className="text-emerald-400 ml-1">Full autonomy pending</span> - currently staff-conducted viewings.
        </p>
      </div>
    </div>
  );
};

export default VaaSLandlordPage;
